import { Suspense, lazy, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import { CartProvider, useCart } from "./contexts/CartContext";
import { CookieConsentProvider } from "./contexts/CookieConsentContext";
import LanguageRoutes from "./components/LanguageRoutes";
import AnalyticsProvider from "./components/analytics/AnalyticsProvider";
import WhatsAppLeadTracker from "./components/analytics/WhatsAppLeadTracker";
import ExitIntentModal from "./components/ExitIntentModal";
import ExcludedPageHead from "./components/seo/ExcludedPageHead";

const SeoMonitor = lazy(() => import("./pages/SeoMonitor"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminOverview = lazy(() => import("./pages/admin/AdminOverview"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));
const AdminBlogDrafts = lazy(() => import("./pages/admin/AdminBlogDrafts"));
const AdminLandingPages = lazy(() => import("./pages/admin/AdminLandingPages"));
const AdminKeywordTracker = lazy(() => import("./pages/admin/AdminKeywordTracker"));
const Login = lazy(() => import("./pages/Login"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));


// Hidden landing pages (indexable, but not linked from nav/footer/search)
const LpCapeVerdeFromUSA = lazy(() => import("./pages/lp/CapeVerdeFromUSA"));
const LpCapeVerdeanHeritage = lazy(() => import("./pages/lp/CapeVerdeanHeritageTrip"));
const LpKapverdenDachNl = lazy(() => import("./pages/lp/KapverdenDachNl"));

// Lazy load CartDrawer: heavy (imports tours dataset) and only mounted when cart is opened
const CartDrawer = lazy(() => import("./components/cart/CartDrawer"));

const queryClient = new QueryClient();

// Mounts CartDrawer the first time the user opens the cart and keeps it mounted thereafter
// so that Radix open/close animations work correctly. Keeps the cart dataset (tours)
// out of the initial bundle.
const LazyCartDrawer = () => {
  const { isCartOpen } = useCart();
  const [hasOpened, setHasOpened] = useState(false);

  useEffect(() => {
    if (isCartOpen && !hasOpened) setHasOpened(true);
  }, [isCartOpen, hasOpened]);

  if (!hasOpened) return null;
  return (
    <Suspense fallback={null}>
      <CartDrawer />
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <CartProvider>
        <CookieConsentProvider>
          <Toaster />
          <Sonner />
          <Suspense fallback={null}>
            <BrowserRouter>
              <AnalyticsProvider>
                <WhatsAppLeadTracker />
                <ExitIntentModal />
                <LazyCartDrawer />
                <ScrollToTop />
                <Routes>
                  {/* Admin hub (PIN-gated, noindex) */}
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminOverview />} />
                    <Route path="seo" element={<SeoMonitor />} />
                    <Route path="leads" element={<AdminLeads />} />
                    <Route path="blog-drafts" element={<AdminBlogDrafts />} />
                    <Route path="landing-pages" element={<AdminLandingPages />} />
                    <Route path="keywords" element={<AdminKeywordTracker />} />
                  </Route>

                  {/* Auth + MCP OAuth consent (noindex) */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/.lovable/oauth/consent" element={<><ExcludedPageHead title="Account authorization" /><OAuthConsent /></>} />

                  {/* Internal SEO monitor alias (noindex) */}
                  <Route path="/seo-monitor" element={<SeoMonitor />} />


                  {/* Hidden country-specific landing pages, indexable for SEO, not linked anywhere */}
                  <Route path="/lp/cape-verde-vacation-from-usa" element={<LpCapeVerdeFromUSA />} />
                  <Route path="/lp/cape-verde-heritage-trip-for-cape-verdeans-abroad" element={<LpCapeVerdeanHeritage />} />
                  <Route path="/lp/cape-verde-heritage-trip-for-cape-verdean-americans" element={<Navigate to="/lp/cape-verde-heritage-trip-for-cape-verdeans-abroad" replace />} />
                  <Route path="/lp/kapverden-urlaub-deutschland-schweiz-niederlande" element={<LpKapverdenDachNl />} />

                  {/* Language routes with explicit parameter */}
                  <Route path="/de/*" element={<LanguageRoutes language="de" />} />
                  <Route path="/fr/*" element={<LanguageRoutes language="fr" />} />
                  <Route path="/it/*" element={<LanguageRoutes language="it" />} />
                  <Route path="/es/*" element={<LanguageRoutes language="es" />} />

                  {/* Default (English) routes - no prefix */}
                  <Route path="/*" element={<LanguageRoutes language="en" />} />
                </Routes>
              </AnalyticsProvider>
            </BrowserRouter>
          </Suspense>
        </CookieConsentProvider>
      </CartProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
